import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const Udaipur = () => {
  const tourData = {
    name: "Udaipur",
    duration: "03 Nights 04 Days",
    price: "₹ 5,999/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755110856/jw3pxzoqcgtejuyvsogx.jpg",
    altitude: "598 m",
    difficulty: "Easy",
    region: "Rajasthan",
    highlights: ["Lake Pichola", "City Palace", "Cultural experience"],
    bestTime: "October to March",
    description: "The Udaipur tour is a perfect weekend getaway in Rajasthan that combines royal experiences with scenic beauty. This easy tour offers palace visits, lake rides, and cultural exploration, making it ideal for families and adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Udaipur Tour - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Udaipur tour in Rajasthan. Weekend getaway with palace visits and lake experiences." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Weekend getaway with palace visits and lake experiences
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This tour takes you to the beautiful City of Lakes, Udaipur in Rajasthan, 
                    offering a perfect blend of royal experiences and scenic beauty. The journey 
                    includes palace visits, lake rides, temple visits, and cultural exploration, 
                    making it an ideal weekend escape.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Lake Pichola boat ride</li>
                    <li>City Palace exploration</li>
                    <li>Cultural experiences</li>
                    <li>Mount Abu visit</li>
                    <li>Dilwara Temples</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Local market shopping</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 1: Depart from Delhi</h4>
                    <ul>
                      <li>Depart from Delhi at night for an overnight journey</li>
                      <li>Travel to Udaipur in Rajasthan</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Udaipur Exploration</h4>
                    <ul>
                      <li>Morning: Arrive in Udaipur and check into accommodation</li>
                      <li>Afternoon: Explore City Palace and Lake Pichola</li>
                      <li>Evening: Boat ride and local market visit</li>
                      <li>Meals: Breakfast, Lunch, Dinner</li>
                      <li>Stay: Hotel in Udaipur</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Mount Abu Visit</h4>
                    <ul>
                      <li>Morning: After breakfast, drive to Mount Abu</li>
                      <li>Daytime: Visit Dilwara Temples and Nakki Lake</li>
                      <li>Explore Sunset Point and local attractions</li>
                      <li>Evening: Return to Udaipur for dinner and overnight stay</li>
                      <li>Meals: Breakfast, Lunch, Dinner</li>
                      <li>Stay: Hotel in Udaipur</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 4: Return to Delhi</h4>
                    <ul>
                      <li>Morning: Breakfast and check-out from accommodation</li>
                      <li>Visit to local market for souvenirs</li>
                      <li>Afternoon: Depart for Delhi</li>
                      <li>Evening: Arrive in Delhi</li>
                      <li>Meals: Breakfast</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation: All transfers from Delhi to Delhi</li>
                    <li>Meals: 2 breakfasts, 2 lunches, and 2 dinners</li>
                    <li>2 Nights Stay in Hotel at Udaipur</li>
                    <li>Boat ride on Lake Pichola</li>
                    <li>Professional tour guide</li>
                    <li>24/7 customer support</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>5% GST if applicable</li>
                    <li>Personal expenses and tips</li>
                    <li>Food on the way to and from Pick and Drop</li>
                    <li>Any additional activities not mentioned</li>
                    <li>Travel insurance</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>598 meters</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Tour Distance:</strong>
                      <span>1,000 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Easy</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>October to March</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>QUAD SHARING:</strong> ₹ 5,999 + 5% GST</li>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 6,999 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 7,499 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Lake Pichola boat ride</li>
                    <li>City Palace exploration</li>
                    <li>Cultural experiences</li>
                    <li>Mount Abu visit</li>
                    <li>Dilwara Temples</li>
                    <li>Easy difficulty level</li>
                    <li>Perfect for families</li>
                    <li>Local market shopping</li>
                    <li>Professional guide support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default Udaipur;
